import { Device, MarketLocation } from '@e2m-customer-portal/hooks';
import { ForecastProperty } from 'generated/graphql';
import { ReactElement, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../dialog/Dialog';
import { DowntimeModalContent } from './DowntimeModalContent';

type Reason = 'backdropClick' | 'escapeKeyDown' | 'cancelClick' | 'successClick';

type OnCloseFunc<E extends SyntheticEvent = SyntheticEvent> = (event: E | null, reason: Reason) => void;

export interface DowntimeModalProps {
  /**
   * Sets whether the dialog should be open.
   */
  open: boolean;

  /**
   * The marketLocation to use the devices from.
   */
  marketLocation: MarketLocation;
  device: Device;

  /**
   * Whether the downtime should be set according to a 15 min resolution of the schedule.
   */
  currentForecastProperty?: ForecastProperty;

  /**
   * Gets triggered when the dialog should close. The consumer of `DowntimeModal` should then set `open` to false.
   * The given reason indicates the source of the close command. If the source was the submit button, then all
   * required fields were sent to the backend automatically.
   */
  onClose: OnCloseFunc;
}

export const DowntimeModal = ({
  open,
  marketLocation,
  device,
  currentForecastProperty,
  onClose,
}: Readonly<DowntimeModalProps>): ReactElement<DowntimeModalProps> => {
  const [t] = useTranslation();

  const handleClose = (event: SyntheticEvent | null, reason: Reason): void => {
    if (reason === 'backdropClick') {
      event?.stopPropagation();
      return;
    }

    onClose(event, reason);
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t('DOWNTIME_MODAL_TITLE')}>
      <DowntimeModalContent
        marketLocation={marketLocation}
        device={device}
        currentForecastProperty={currentForecastProperty}
        onClose={handleClose}
      />
    </Dialog>
  );
};
